import React, { useState } from 'react';
import {
  Button,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  ContextMenu,
  ContextMenuItem,
  Flashy,
  CondensedHeader,
  CondensedSmall,
  CondensedMedium,
  ClipboardUtil,
} from '@axiom/ui';
import type {
  AhqSubmission,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';

import { EnvUtil } from '../../utils/env-util';
import { DateUtil } from '../../utils/date-util';
import { SubmissionsLegacyApi } from '../../api/submissions-legacy-api';
import { OpportunityApi } from '../../api/opportunity-api';

import { OpportunitySubmissionCopyTalentSummaries } from './OpportunitySubmissionCopyTalentSummaries';
import { OpportunitySubmissionUtil } from './opportunity-submission-util';

type OpportunitySubmissionURLProps = {
  submission: AhqSubmission;
  opportunity: Opportunity;
  candidates: OpportunityCandidate[];
  readOnly?: boolean;
  name?: string;
};

export const OpportunitySubmissionUrl = ({
  submission: {
    submissionId,
    accountId,
    candidateOpportunities,
    submissionLastUpdatedAt: lastUpdated,
    submissionPublishedAt,
    newSubmissionType,
  },
  candidates,
  opportunity,
  readOnly,
  name,
}: OpportunitySubmissionURLProps) => {
  const [showCopyTalentSummaries, setShowCopyTalentSummaries] = useState(false);
  const hasAnyCuratedSubmittableDescription = candidateOpportunities.some(c =>
    OpportunitySubmissionUtil.hasSubmittableDescription({
      candidate: c as unknown as OpportunityCandidate,
      opportunity,
    })
  );

  const AFCLink = `${EnvUtil.clientUrl}/account/${accountId}/submission/${submissionId}/matches`;
  const updatedText =
    lastUpdated && `Last updated: ${DateUtil.displayTimestamp(lastUpdated)}`;

  const { activeCuratedCandidates } = OpportunitySubmissionUtil.groupCandidates(
    {
      candidates,
      opportunity,
    }
  );
  const hasCuratedTalent = activeCuratedCandidates.length > 0;

  const handlePublish = async () => {
    await OpportunityApi.updatePublishOpportunity({
      opportunityId: opportunity.id,
    });
    await SubmissionsLegacyApi.updateSubmission(submissionId, {
      newSubmissionType,
      candidates: candidateOpportunities.map(canOpp => ({
        candidateId: canOpp.candidate.id,
        isActiveForSubmission: !!canOpp.isActiveForSubmission,
      })),
    });
    await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
      submissionId,
      opportunityId: opportunity.id,
    });
  };

  return (
    <>
      {!!submissionPublishedAt && (
        <Gutter bottom={readOnly ? '0px' : '8px'}>
          <CondensedHeader>It's live!</CondensedHeader>
          <Gutter bottom="4px" />
          <CondensedMedium name="SUBMISSION_URL_BANNER">
            The AFC submission link has been published and is visible in AFC.
            You can now send the submission email to clients.
          </CondensedMedium>
        </Gutter>
      )}
      {readOnly ? (
        <CondensedSmall name="READONLY_HEADER">
          <Flashy color="textReversedSecondary">{opportunity.stage}</Flashy>
        </CondensedSmall>
      ) : (
        <>
          <div data-test="SUBMISSION_URL_HEADER_BLOCK">
            <CondensedHeader name="SUBMISSION_URL_HEADER_TITLE">
              Axiom for Clients Submission Link
            </CondensedHeader>
          </div>
          <Gutter bottom="4px" />
          <div data-test="SUBMISSION_URL_BODY">
            {submissionPublishedAt ? (
              <Layout position="middle">
                <LayoutItem fluid>
                  <CondensedMedium
                    className="truncate"
                    name="SUBMISSION_URL_BOX"
                  >
                    {AFCLink}
                  </CondensedMedium>
                  <Gutter bottom="4px" />
                  <CondensedSmall name="UPDATED_ON">
                    <Flashy color="textReversedSecondary">{updatedText}</Flashy>
                  </CondensedSmall>
                </LayoutItem>
                <LayoutItem>
                  <Layout inline position="middle">
                    <LayoutItem>
                      <IconButton
                        name="SUBMISSION_ICON_OPEN_TAB"
                        icon="pop-out"
                        pattern="secondary"
                        variation="minimal"
                        toTab={AFCLink}
                        title={`Open "Submission" in new tab`}
                      />
                    </LayoutItem>
                    <LayoutItem>
                      <ContextMenu
                        direction="left"
                        name="SUBMISSION_URL_CONTEXT_MENU"
                      >
                        <ContextMenuItem
                          name="COPY_AFC_LINK"
                          onClick={async () =>
                            ClipboardUtil.copy({
                              text: AFCLink,
                            })
                          }
                        >
                          Copy AFC submission link
                        </ContextMenuItem>
                        <ContextMenuItem
                          name="DOWNLOAD_INDIVIDUAL_PDF_LINK"
                          onClick={() => {
                            window.open(
                              `${EnvUtil.clientUrl}/account/${accountId}/${submissionId}/pdf/split`,
                              'download_pdf'
                            );
                          }}
                        >
                          Download Individual talent submission PDFs
                        </ContextMenuItem>
                        {hasAnyCuratedSubmittableDescription ? (
                          <ContextMenuItem
                            name="COPY_TALENT_SUMMARIES"
                            onClick={() => {
                              setShowCopyTalentSummaries(true);
                            }}
                          >
                            Copy talent summaries to email
                          </ContextMenuItem>
                        ) : null}
                      </ContextMenu>
                    </LayoutItem>
                  </Layout>
                </LayoutItem>
              </Layout>
            ) : (
              <>
                <CondensedMedium name="SUBMISSION_URL_UNPUBLISHED_BOX">
                  <Flashy color="textReversedSecondary">
                    Submissions URL will be visible once the submission has been
                    published. Publishing a submission makes it visible in AFC.
                  </Flashy>
                </CondensedMedium>
                <Gutter bottom="8px" />

                <Layout position="right">
                  <LayoutItem>
                    <Button
                      name="PUBLISH_SUBMISSION_BUTTON"
                      pattern="secondary"
                      onClick={handlePublish}
                      disabled={!hasCuratedTalent}
                    >
                      PUBLISH
                    </Button>
                  </LayoutItem>
                </Layout>
              </>
            )}
          </div>
        </>
      )}
      {showCopyTalentSummaries ? (
        <OpportunitySubmissionCopyTalentSummaries
          candidates={candidateOpportunities}
          opportunity={opportunity}
          AFCLink={AFCLink}
          onClose={() => {
            setShowCopyTalentSummaries(false);
          }}
        />
      ) : null}
    </>
  );
};
